import { useMemo, useEffect, useState, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { Text } from "@/components/Primitives/Text"
import { Button } from "@/components/Primitives/Button"
import { Card } from "@/components/Primitives/Card"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useConfig } from "@/hooks/useConfig"
import { QuestLayout } from "@/components/Complex/QuestLayout"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  getSingleJourney,
  getPublicMission,
  completeMissionAttempt,
  acceptJourneyMission,
} from "@/lib/api/quests"
import { isStageNavigable } from "@/lib/utils/stageNavigation"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"
import { queryKeys } from "@/lib/utils/query"
import { MissionResourceContainer } from "@/lib/components/MissionResourceContainer"
import { isAuthenticated } from "@/hooks/useLocalStorage"
import { JourneyMissionInfo, Mission } from "@/types/quests"
import { useJourneys } from "@/hooks/useJourneys"
import { NotFoundPage } from "./NotFoundPage"
import { QuestionForm } from "@/components/Complex/QuestionForm"
import { submitMissionQuestionAnswer } from "@/lib/api/quests"
import { ShareButton } from "@/components/Primitives/ShareButton"
import { Question } from "@/types/question"
import { toast } from "sonner"
import { MissionQuestion } from "@/types/quests"
import { Estimate } from "@/components/Complex/Estimate"

const mapMissionQuestionToQuestion = (q: MissionQuestion): Question => ({
  question_string: q.question_string,
  question_code: q.question_code,
  kind: q.kind,
  choices: q.choices,
  current_value: 0,
})

const getMissionUrl = (mission: Mission): string => {
  if (mission.short_code) {
    return `${import.meta.env.VITE_URL_SHORTENER}/${mission.short_code}`
  }

  return window.location.href
}

export function MissionPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { missionId } = useParams<{ missionId: string }>()
  const { t, tp } = useConfig()
  const queryClient = useQueryClient()
  const [accordionValue, setAccordionValue] = useState<string>("")
  const [showQuestions, setShowQuestions] = useState<boolean>(false)
  const [questionAnswers, setQuestionAnswers] = useState<
    Record<string, number>
  >({})
  const userIsAuthenticated = isAuthenticated()
  const {
    journeys,
    loading: journeysLoading,
    setCurrentMission,
    setCurrentJourney,
  } = useJourneys(userIsAuthenticated)

  const overrideBody = useMemo(() => {
    const fragment = location.hash
    if (!fragment.startsWith("#body=")) {
      return null
    }

    const encodedBody = fragment.substring(6) // Remove prefix
    try {
      return decodeURIComponent(encodedBody)
    } catch {
      return "**Error decoding**"
    }
  }, [location.hash])

  const {
    data: publicMission,
    isLoading: isPublicMissionLoading,
    error: publicMissionError,
  } = useQuery({
    queryKey: ["publicMission", missionId],
    queryFn: () => getPublicMission(missionId!),
    enabled: !!missionId,
    retry: false, // Don't retry on 404
  })

  const journeyMissionInfo = useMemo<JourneyMissionInfo | null>(() => {
    if (journeysLoading || journeys.length === 0) return null

    for (const journey of journeys) {
      if (!journey.journey_stages) continue

      for (const journeyStage of journey.journey_stages) {
        if (!journeyStage.journey_missions) continue

        const journeyMission = journeyStage.journey_missions.find(
          (journeyMission) => journeyMission.mission.id === missionId,
        )
        if (journeyMission) {
          return {
            journeyMission: {
              id: journeyMission.id,
              mission: journeyMission.mission,
              state: journeyMission.state,
              attempt_id: journeyMission.attempt_id!,
            },
            stageName: journeyStage.stage.title,
            stageId: journeyStage.id,
            journeyId: journey.id,
            questTitle: journey.quest.title,
            questId: journey.quest.id,
          }
        }
      }
    }
    return null
  }, [journeys, missionId, journeysLoading])

  const mission = journeyMissionInfo?.journeyMission
  const stageName = journeyMissionInfo?.stageName || ""
  const journeyId = journeyMissionInfo?.journeyId || ""
  const isCompleted = mission?.state === "OK"
  const journeyIdForJourney = journeyMissionInfo?.journeyId || ""
  const { data: currentJourney, isLoading: isJourneyLoading } = useQuery({
    queryKey: queryKeys.journey(journeyIdForJourney),
    queryFn: () => getSingleJourney(journeyIdForJourney),
    enabled: !!journeyIdForJourney && userIsAuthenticated,
  })

  const journeyStages = useMemo(() => {
    return currentJourney?.journey_stages || []
  }, [currentJourney])

  const calculateStageProgress = useCallback(
    (journeyStageId: string) => {
      const journeyStage = journeyStages.find(
        (journeyStage) => journeyStage.id === journeyStageId,
      )
      if (!journeyStage) return 0

      return Math.round(
        100 * (journeyStage.completed_points / journeyStage.total_points),
      )
    },
    [journeyStages],
  )

  const completedJourneyStageIds = useMemo(() => {
    const completedIds = journeyStages
      .filter((journeyStage) => journeyStage.state === "OK")
      .map((journeyStage) => journeyStage.id)
    return new Set(completedIds)
  }, [journeyStages])

  const currentJourneyStageIndex = useMemo(() => {
    if (!journeyMissionInfo || !journeyStages.length) return -1

    return journeyStages.findIndex(
      (journeyStage) => journeyStage.id === journeyMissionInfo.stageId,
    )
  }, [journeyMissionInfo, journeyStages])

  useEffect(() => {
    if (journeyMissionInfo) {
      setCurrentMission(journeyMissionInfo.journeyMission)
      const journey =
        journeys.find(
          (journey) => journey.id === journeyMissionInfo.journeyId,
        ) || null
      setCurrentJourney(journey)
    }
  }, [journeyMissionInfo, journeys, setCurrentJourney, setCurrentMission])

  const acceptMission = useMutation({
    mutationFn: async (missionId: string) => acceptJourneyMission(missionId),
    onSuccess: () => {
      // Invalidate both journey and journeys data to get the updated attempt_id
      queryClient.invalidateQueries({
        queryKey: queryKeys.journey(journeyIdForJourney),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.journeys,
      })
    },
    onError: (error) => {
      console.error("Error accepting mission:", error)
    },
  })

  // Auto-accept mission if attempt_id is null and user is authenticated
  useEffect(() => {
    if (
      userIsAuthenticated &&
      journeyMissionInfo?.journeyMission &&
      !journeyMissionInfo.journeyMission.attempt_id &&
      !acceptMission.isPending
    ) {
      acceptMission.mutate(journeyMissionInfo.journeyMission.id)
    }
  }, [userIsAuthenticated, journeyMissionInfo])

  const handleStageNavigable = useCallback(
    (index: number) => {
      return isStageNavigable(
        index,
        journeyStages,
        currentJourneyStageIndex,
        completedJourneyStageIds,
      )
    },
    [journeyStages, currentJourneyStageIndex, completedJourneyStageIds],
  )

  const isStageCompletedAfterThisMission = useCallback(() => {
    if (!journeyMissionInfo || !journeyStages.length) return false

    const currentStage = journeyStages.find(
      (journeyStage) => journeyStage.id === journeyMissionInfo.stageId,
    )
    if (!currentStage) return false

    const otherMissionsCompleted = currentStage.journey_missions
      .filter((mission) => mission.id !== missionId)
      .every((mission) => mission.state === "OK")
    return otherMissionsCompleted
  }, [journeyMissionInfo, journeyStages, missionId])

  const findNextIncompleteStage = useCallback(() => {
    if (!journeyStages.length || currentJourneyStageIndex === -1) return null

    for (let i = currentJourneyStageIndex + 1; i < journeyStages.length; i++) {
      const stage = journeyStages[i]
      if (stage.state === "OK") continue

      if (stage.journey_missions.length > 0) {
        return {
          stageIndex: i,
          stageId: stage.id,
          firstMissionId: stage.journey_missions[0].id,
        }
      }
    }
    return null
  }, [journeyStages, currentJourneyStageIndex])

  const handleNavigationAfterCompletion = useCallback(() => {
    const stageCompleted = isStageCompletedAfterThisMission()
    const nextStage = findNextIncompleteStage()
    const nextStageId = nextStage?.stageId
    const questId = journeyMissionInfo?.questId

    if (stageCompleted) {
      toast.success("Stage completed!")

      if (nextStageId) {
        navigate(`/quests/${questId}?stage=${nextStageId}`)
      } else {
        navigate(`/quests/${questId}?view=complete`)
      }
    } else {
      navigate(`/quests/${questId}`)
    }
  }, [
    isStageCompletedAfterThisMission,
    findNextIncompleteStage,
    journeyMissionInfo,
    navigate,
  ])

  const completeMission = useMutation({
    mutationFn: async (attemptId: string) => completeMissionAttempt(attemptId),
    onSuccess: async () => {
      toast.success("Mission completed successfully!")
      queryClient.invalidateQueries({
        queryKey: queryKeys.journey(journeyIdForJourney),
      })

      if (journeyIdForJourney) {
        try {
          const freshJourney = await getSingleJourney(journeyIdForJourney)
          setCurrentJourney(freshJourney)
        } catch (err) {
          console.error("Error refreshing quest after mission complete", err)
        }
      }

      const questions = publicMission?.questions ?? []
      const hasQuestions = questions.length > 0
      if (hasQuestions) {
        setShowQuestions(true)
      } else {
        handleNavigationAfterCompletion()
      }
    },
    onError: () => {
      toast.error("Error completing mission, please try again.")
    },
  })

  const handleAnswerChange = useCallback(
    (questionCode: string, value: number | string) => {
      if (!journeyMissionInfo) return

      setQuestionAnswers((prev) => ({
        ...prev,
        [questionCode]: typeof value === "string" ? 1 : value,
      }))

      submitMissionQuestionAnswer(
        journeyMissionInfo.journeyMission.id,
        questionCode,
        value.toString(),
      )
    },
    [journeyMissionInfo],
  )

  // Handler for completing all questions
  const handleQuestionsComplete = useCallback(() => {
    setShowQuestions(false)
    handleNavigationAfterCompletion()
  }, [handleNavigationAfterCompletion])

  const journeyProgress = useMemo(() => {
    if (!currentJourney) return 20

    return (100 * currentJourney.completed_points) / currentJourney.total_points
  }, [currentJourney])

  if (isPublicMissionLoading || (userIsAuthenticated && journeysLoading)) {
    return (
      <div className="container mx-auto p-6">
        <Text variant="lead">
          {t("config_mission_loading") || "Loading mission…"}
        </Text>
      </div>
    )
  }

  if (publicMissionError || (!publicMission && !isPublicMissionLoading)) {
    return <NotFoundPage />
  }

  if (isJourneyLoading || !publicMission) {
    return (
      <div className="container mx-auto p-6">
        <Text variant="lead">{t("config_mission_loading")}</Text>
      </div>
    )
  }

  const handleMarkAsCompleted = () => {
    if (!journeyMissionInfo || !journeyMissionInfo.journeyMission.attempt_id) {
      console.error("handleMarkAsCompleted has no attempt_id")
      return
    }
    completeMission.mutate(journeyMissionInfo.journeyMission.attempt_id)
  }

  const footerContent = showQuestions ? null : isCompleted ? (
    <Button
      onClick={() => navigate(-1)}
      variant="tertiary"
      className="w-full md:w-auto"
    >
      {t("config_mission_button_back")}
    </Button>
  ) : (
    <Button
      onClick={handleMarkAsCompleted}
      disabled={completeMission.isPending}
      className="bg-purple-500 hover:bg-purple-600 text-neutral-50 w-full md:w-auto"
    >
      {completeMission.isPending
        ? t("config_mission_button_sending")
        : t("config_button_complete")}
    </Button>
  )

  const handleStageSelect = (index: number) => {
    if (!journeyStages || !journeyStages[index]) return

    setAccordionValue("")

    if (journeyId) {
      const selectedStage = journeyStages[index]
      const selectedStageId = selectedStage.id
      const questId = currentJourney?.quest?.id

      navigate(`/quests/${questId}?view=details&stageId=${selectedStageId}`)
    }
  }

  const shareMessage = tp("config_share_message", {
    missionName: publicMission?.title || "",
  })

  const shareButton = (
    <ShareButton
      url={getMissionUrl(publicMission)}
      title={shareMessage}
      text={shareMessage}
    />
  )

  const missionContent = (
    <div className="space-y-8">
      <div className="mb-4">
        <MarkdownRenderer content={overrideBody ?? publicMission.body ?? ""} />
      </div>
      <MissionResourceContainer resources={publicMission.resources} />
    </div>
  )

  let content
  if (showQuestions) {
    const questions: Question[] = publicMission.questions.map(
      mapMissionQuestionToQuestion,
    )
    content = (
      <QuestionForm
        questions={questions}
        answers={questionAnswers}
        onAnswerChange={handleAnswerChange}
        onSubmit={handleQuestionsComplete}
        isSubmitting={false}
        showSkip={true}
        showProgress={true}
      />
    )
  } else {
    content = missionContent
  }

  const actualFooterContent = footerContent ? (
    <div className="flex justify-center gap-2 flex-col-reverse md:justify-start md:flex-row">
      {shareButton}
      {footerContent}
    </div>
  ) : null

  return (
    <div className="-mx-2 w-full">
      <Card className="p-4">
        {userIsAuthenticated && journeyMissionInfo ? (
          <QuestLayout
            questName={journeyMissionInfo.questTitle}
            journeyProgress={journeyProgress}
            stageName={stageName}
            missionName={publicMission.title}
            questId={journeyMissionInfo.questId}
            stages={journeyStages}
            currentStageIndex={currentJourneyStageIndex}
            completedStages={completedJourneyStageIds}
            isStageNavigable={handleStageNavigable}
            footerContent={actualFooterContent}
            accordionValue={accordionValue}
            setAccordionValue={setAccordionValue}
            onStageSelect={handleStageSelect}
            calculateStageProgress={calculateStageProgress}
            prep_time={publicMission.prep_time}
            action_time={publicMission.action_time}
          >
            {content}
          </QuestLayout>
        ) : (
          <div className="p-4 container mx-auto space-y-8">
            <Text variant="h1" className="mb-4">
              {publicMission.title}
            </Text>
            <Estimate
              prep_time={publicMission.prep_time}
              action_time={publicMission.action_time}
            />
            {missionContent}
            <div className="border-t pt-4">
              <div className="flex justify-center flex-col-reverse gap-2 md:justify-end md:flex-row">
                {shareButton}
                <Button asChild>
                  <Link to="/signup">{t("config_mission_button_signup")}</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
